import { components } from "@/api-schema";
import { useCompanyGroupSearchQuery } from "@/common/api/company-group/use-company-group-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["CompanyGroupSearchBody"];

const FILTER_PAGE_SIZE = 50;
const companyGroupNameCollator = new Intl.Collator("de", { sensitivity: "base", numeric: true });

function useCompanyGroupFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: companyGroups,
    isPending: isLoading,
    error,
  } = useCompanyGroupSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options = buildCompanyGroupFilterOptions(companyGroups?.data);
  const loadedCount = companyGroups?.data?.length ?? 0;
  const total = companyGroups?.meta?.total ?? 0;
  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class CompanyGroupFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "companyGroup";
  readonly labelSingular = "masterdata:CompanyGroup";
  readonly labelPlural = "masterdata:CompanyGroups";
  readonly placeholder = "masterdata:SelectCompanyGroup";
  readonly searchPlaceholder = "masterdata:SearchCompanyGroups";
  readonly emptyText = "masterdata:NoCompanyGroupsFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <CompanyGroupFilterComponent filter={this} {...props} />;
  }
}

function CompanyGroupFilterComponent({ filter, ...props }: { filter: CompanyGroupFilter } & BaseFilterProps) {
  const { data, pagination } = useCompanyGroupFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}

export function buildCompanyGroupFilterOptions(
  companyGroups?: components["schemas"]["CompanyGroup"][],
): FilterData["options"] {
  if (!companyGroups?.length) {
    return [];
  }

  const options: FilterData["options"] = [];
  const seenIds = new Set<string>();

  for (const companyGroup of companyGroups) {
    const id = companyGroup.uuid?.trim();
    const name = companyGroup.name?.trim();

    if (!id || !name || seenIds.has(id)) {
      continue;
    }

    seenIds.add(id);
    options.push({ id, name });
  }

  options.sort((left, right) => companyGroupNameCollator.compare(left.name, right.name));

  return options;
}
